/* eslint-disable @typescript-eslint/naming-convention */
import {
  IFdApplicationType,
  IFdFinancialPerformance,
  IFdOperatingCost,
  IFdProductionCost,
} from 'constants/interfaces';

import {lshParseOperatingCostMonthlyCosts} from './operatingCostMonthlyCosts';

export type MetricKey =
  | 'revenue'
  | 'grossProfit'
  | 'costOfSales'
  | 'operatingCosts'
  | 'netProfit';

export interface MiniPLData {
  years: number[];
  data: Record<number, Record<MetricKey, number>>;
}

const sumProductionCosts = (pcs: IFdProductionCost[]): Record<number, number> =>
  pcs.reduce<Record<number, number>>((acc, pc) => {
    const yr =
      typeof pc.year === 'number' && Number.isFinite(pc.year)
        ? pc.year
        : new Date(pc.plantingDate ?? '').getFullYear();
    const total =
      Number(pc.seed ?? 0) +
      Number(pc.fertiliser ?? 0) +
      Number(pc.chemical ?? 0);
    if (!Number.isFinite(yr)) return acc;
    acc[yr] = (acc[yr] ?? 0) + total;
    return acc;
  }, {});

const sumOperatingCosts = (ocs: IFdOperatingCost[]): Record<number, number> =>
  ocs.reduce<Record<number, number>>((acc, oc) => {
    const yr = oc.year;
    const total = lshParseOperatingCostMonthlyCosts(oc.monthlyCosts).total;
    acc[yr] = (acc[yr] ?? 0) + total;
    return acc;
  }, {});

export const buildMiniPLData = (
  application?: IFdApplicationType | null,
): MiniPLData => {
  if (!application) return {years: [], data: {}};

  const out: Record<number, Record<MetricKey, number>> = {};
  const yearSet = new Set<number>();

  (application.financialPerformance ?? []).forEach(
    (fp: IFdFinancialPerformance) => {
      yearSet.add(fp.year);
      const revenue = Number(fp.revenue ?? 0);
      const expenses = Number(fp.expenses ?? 0);
      const grossProfit = revenue - expenses;

      out[fp.year] = {
        revenue,
        grossProfit,
        costOfSales: expenses,
        operatingCosts: Number(fp.operatingCosts ?? 0),
        netProfit: Number(fp.profit ?? grossProfit),
      };
    },
  );

  const prodTotals = sumProductionCosts(application.productionCosts ?? []);
  const opTotals = sumOperatingCosts(application.operatingCosts ?? []);

  Object.keys({...prodTotals, ...opTotals}).forEach((y) =>
    yearSet.add(Number(y)),
  );

  yearSet.forEach((yr) => {
    if (!out[yr]) {
      out[yr] = {
        revenue: 0,
        grossProfit: 0,
        costOfSales: 0,
        operatingCosts: 0,
        netProfit: 0,
      };
    }

    if (!out[yr].costOfSales) {
      out[yr].costOfSales = Number(prodTotals[yr] ?? 0);
    }

    // Use calculated opTotals if no saved operatingCosts exist
    out[yr].operatingCosts = Number(
      opTotals[yr] ?? out[yr].operatingCosts ?? 0,
    );

    out[yr].grossProfit = out[yr].revenue - out[yr].costOfSales;

    out[yr].netProfit = out[yr].grossProfit - out[yr].operatingCosts;
  });

  const years = Array.from(yearSet).sort((a, b) => b - a);
  return {years, data: out};
};
